import JSZip from "jszip";

type FolderDocument = {
  name: string;
  path: string;
  treePath?: string;
};

const getAuthHeaders = (): HeadersInit => {
  const token =
    localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

  return token ? { Authorization: `Bearer ${token}` } : {};
};

const sanitizeZipSegment = (value: string) =>
  value
    .replace(/[<>:"\\|?*\u0000-\u001F]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\.+$/, "")
    || "document";

const getUniquePath = (path: string, usedPaths: Set<string>) => {
  if (!usedPaths.has(path)) {
    usedPaths.add(path);
    return path;
  }

  const lastSlashIndex = path.lastIndexOf("/");
  const directory = lastSlashIndex >= 0 ? `${path.slice(0, lastSlashIndex + 1)}` : "";
  const filename = lastSlashIndex >= 0 ? path.slice(lastSlashIndex + 1) : path;
  const dotIndex = filename.lastIndexOf(".");
  const name = dotIndex > 0 ? filename.slice(0, dotIndex) : filename;
  const extension = dotIndex > 0 ? filename.slice(dotIndex) : "";

  let index = 2;
  let candidate = `${directory}${name} (${index})${extension}`;
  while (usedPaths.has(candidate)) {
    index += 1;
    candidate = `${directory}${name} (${index})${extension}`;
  }

  usedPaths.add(candidate);
  return candidate;
};

const getRelativeZipPath = (doc: FolderDocument, basePath: string) => {
  const docPath = doc.treePath ?? basePath;
  const relativeFolder = docPath.startsWith(`${basePath} / `)
    ? docPath.slice(basePath.length + 3)
    : "";

  const segments = relativeFolder
    .split(" / ")
    .map(sanitizeZipSegment)
    .filter(Boolean);

  return [...segments, sanitizeZipSegment(doc.name || "document")].join("/");
};

export const downloadDocumentFolder = async ({
  documents,
  basePath,
  filename,
}: {
  documents: FolderDocument[];
  basePath: string;
  filename: string;
}) => {
  const zip = new JSZip();
  const usedPaths = new Set<string>();
  const headers = getAuthHeaders();

  for (const doc of documents) {
    if (!doc.path) {
      continue;
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_ENDPOINT}/media/download/${doc.path}`,
      {
        headers,
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Impossible de télécharger un fichier du dossier");
    }

    const blob = await response.blob();
    zip.file(getUniquePath(getRelativeZipPath(doc, basePath), usedPaths), blob);
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  const blobUrl = URL.createObjectURL(zipBlob);
  const link = document.createElement("a");

  link.href = blobUrl;
  link.download = `${sanitizeZipSegment(filename || "dossier")}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
};
